import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Lock } from 'lucide-react';
import { SpawnRow } from './SpawnRow';
import { documentedAccessLevels, mapPath, monsterPath, prettyAccessName } from '../../utils/worldFormat';
import { derived } from '../../utils/worldQueries';
import { MonsterThumb } from './EntityImage';
import { imageEntryForMonster } from '../../utils/entityImages';
import type { Monster, Spawn, WorldData, WorldMap } from '../../types/world';
const rowCls = 'flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0A1628] px-4 py-3.5 transition-colors duration-150 hover:border-[#D4AF37]/40';
export function MapList({
  w,
  maps,
  pj
}: {
  w: WorldData;
  maps: WorldMap[];
  pj?: number;
}) {
  const d = derived(w);
  return <ul className="grid gap-2.5 md:grid-cols-2" data-id="element-1238" data-mp="p18p8p1zplrt">
      {maps.map(m => {
      const levels = documentedAccessLevels(m);
      const spots = (w.idx.spawnsByMap.get(m.id) ?? []).filter(s => s.kind === 'spot').length;
      const mons = d.mapMonsters.get(m.id)?.size ?? 0;
      const reachable = pj !== undefined ? m.moveEntries.filter(e => e.minLevel !== undefined && e.minLevel <= pj) : [];
      return <li key={m.id} data-id="element-1239" data-mp="21sjoduvju2qo">
            <Link to={mapPath(m.id)} className={rowCls} data-id="element-1240" data-mp="21t1wppvhk7ey">
              <span className="min-w-0 flex-1" data-id="element-1241" data-mp="21umr9fhsvqct">
                <span className="block font-cinzel font-bold text-white" data-id="element-1242" data-mp="1x11xjzn8pijf">{m.name}</span>
                <span className="mt-0.5 block text-sm text-gray-400" data-id="element-1243" data-mp="2asovs50qt14u">
                  {levels.length ? `Acceso desde nivel ${Math.min(...levels)}` : 'Acceso especial o no documentado'}
                  {m.moveEntries.length > 1 && ` · ${m.moveEntries.length} accesos`}
                </span>
                <span className="mt-1 block text-xs text-gray-500" data-id="element-1244" data-mp="2fsb90egkmhdn">{spots} spots · {mons} monstruos documentados</span>
                {pj !== undefined && m.moveEntries.length > 1 && <span className="mt-1 block text-xs text-[#D4AF37]/90" data-id="element-1245" data-mp="lf3aoctdznn9">Con nivel {pj}: {reachable.map(e => prettyAccessName(e.name)).join(', ')}</span>}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1246" data-mp="1f40ya08nv2vq" />
            </Link>
          </li>;
    })}
    </ul>;
}
export function MonsterList({
  w,
  monsters,
  npc = false
}: {
  w: WorldData;
  monsters: Monster[];
  npc?: boolean;
}) {
  const d = derived(w);
  return <ul className="grid gap-2.5 md:grid-cols-2" data-id="element-1247" data-mp="1u1tgx19i87e5">
      {monsters.map(m => {
      const maps = Array.from(d.monsterMaps.get(m.id) ?? []).map(id => w.maps.get(id)?.name ?? `Mapa ${id}`);
      const hasLocation = (w.idx.locationsByMonster.get(m.id)?.length ?? 0) > 0;
      return <li key={m.id} className={imageEntryForMonster(w, m.id)?.image ? `${rowCls} !py-2.5` : undefined} data-id="element-1248" data-mp="27sop0bc6ikgo">
            {/* La imagen queda fuera del enlace: pulsarla no abre la ficha. El nombre sí. */}
            <MonsterThumb entry={imageEntryForMonster(w, m.id)} name={m.name} data-id="element-1249" data-mp="2hqmdmzxyaj9t" />
            <Link to={monsterPath(m.id)} className={imageEntryForMonster(w, m.id)?.image ? 'flex min-w-0 flex-1 items-center gap-3' : rowCls} data-id="element-1250" data-mp="63sm5f29s1pa">
              <span className="min-w-0 flex-1" data-id="element-1251" data-mp="13o1rdy8qmts">
                <span className="flex flex-wrap items-baseline gap-x-2" data-id="element-1252" data-mp="1726utyaha4x">
                  <span className="font-semibold text-white" data-id="element-1253" data-mp="3sb90vza3svrr">{m.name}</span>
                  {!npc && m.level !== undefined && <span className="text-xs text-gray-500" data-id="element-1254" data-mp="3ne0h5u9b4joe">Nv. monstruo {m.level}</span>}
                </span>
                <span className="mt-0.5 block truncate text-sm text-gray-400" data-id="element-1255" data-mp="ow4npzl3myyd">
                  {maps.length ? maps.join(', ') : hasLocation ? '' : 'Ubicación no documentada en los archivos disponibles'}
                </span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1256" data-mp="a4cjojqr97by" />
            </Link>
          </li>;
    })}
    </ul>;
}
export function SpawnList({
  w,
  spawns
}: {
  w: WorldData;
  spawns: Spawn[];
}) {
  return <ul className="grid gap-2.5" data-id="element-1257" data-mp="12rymhps5s7xr">
      {spawns.map(s => <SpawnRow key={s.id} w={w} s={s} data-id="element-1258" data-mp="nb0akptklisc" />)}
    </ul>;
}
export function AccessNote({
  count
}: {
  count: number;
}) {
  if (!count) return null;
  return <p className="mb-4 flex items-start gap-2 text-sm text-gray-400" data-id="element-1259" data-mp="s8s4tqhotgzp">
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1260" data-mp="dff70n8usagh" />
      {count} {count === 1 ? 'mapa no tiene' : 'mapas no tienen'} requisito de acceso documentado y no se incluyen al filtrar por nivel de personaje.
    </p>;
}